import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
import { def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';

const contactVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

const formVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const bgSlides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [bgSlide, setBgSlide] = useState(5); // Start at def6
  const [state, handleSubmit] = useForm("xzzeaqla");

  useEffect(() => {
    const interval = setInterval(() => {
      setBgSlide((prev) => (prev + 1) % bgSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgSlides.length]);

  return (
    <section id="contact" className="py-20 -mt-10 bg-gradient-to-b from-white to-gray-50 dark:from-auxis-charcoal dark:to-auxis-charcoal-light relative overflow-hidden transition-colors duration-300">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={bgSlide}
            src={bgSlides[bgSlide]} 
            alt={`AUXIS Background ${bgSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('contactSection.title')}</h2>
          <div className="w-20 h-1 bg-auxis-gold mx-auto mb-6" />
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t('contactSection.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <motion.div
            variants={contactVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="space-y-8"
          >
            {[
              { Icon: Phone, title: t('contactSection.info.phone.title'), content: t('contactSection.info.phone.value') },
              { Icon: Mail, title: t('contactSection.info.email.title'), content: t('contactSection.info.email.value') },
              { Icon: MapPin, title: t('contactSection.info.address.title'), content: t('contactSection.info.address.value') }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false }}
                transition={{ delay: index * 0.2 }}
                className="flex items-start space-x-4"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  className="p-3 rounded-lg bg-auxis-gold/20"
                >
                  <item.Icon className="w-6 h-6 text-auxis-gold" />
                </motion.div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-300">{item.content}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {state.succeeded ? (
            <motion.div
              variants={formVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              className="flex items-center justify-center h-full"
            >
              <div className="text-center p-8 bg-green-900/30 backdrop-blur-md rounded-lg border border-green-500/30">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('contactSection.form.successTitle', 'Message Envoyé!')}</h3>
                <p className="text-gray-300">{t('contactSection.form.successMessage', 'Merci de nous avoir contacté. Nous vous répondrons dans les plus brefs délais.')}</p>
              </div>
            </motion.div>
          ) : (
            <motion.form
              variants={formVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              className="space-y-6"
              onSubmit={handleSubmit}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: 0 }}
              >
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t('contactSection.form.name.placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 backdrop-blur-md text-white placeholder:text-gray-400 focus:border-auxis-gold focus:ring-1 focus:ring-auxis-gold outline-none transition-all duration-300 hover:border-auxis-gold/50"
                />
                <ValidationError prefix="Name" field="name" errors={state.errors} className="text-red-500 text-sm mt-1" />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: 0.2 }}
              >
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('contactSection.form.email.placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 backdrop-blur-md text-white placeholder:text-gray-400 focus:border-auxis-gold focus:ring-1 focus:ring-auxis-gold outline-none transition-all duration-300 hover:border-auxis-gold/50"
                />
                <ValidationError prefix="Email" field="email" errors={state.errors} className="text-red-500 text-sm mt-1" />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: 0.4 }}
              >
                <textarea
                  id="message"
                  name="message"
                  placeholder={t('contactSection.form.message.placeholder')}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 backdrop-blur-md text-white placeholder:text-gray-400 focus:border-auxis-gold focus:ring-1 focus:ring-auxis-gold outline-none transition-all duration-300 hover:border-auxis-gold/50"
                />
                <ValidationError prefix="Message" field="message" errors={state.errors} className="text-red-500 text-sm mt-1" />
              </motion.div>
              
              <motion.button
                type="submit"
                disabled={state.submitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-8 py-3 bg-auxis-gold text-auxis-charcoal rounded-lg font-semibold hover:bg-white hover:text-auxis-charcoal transition-all duration-300 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {state.submitting ? t('contactSection.form.sending') : t('contactSection.form.submit')}
              </motion.button>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;